import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, SvgIcon, Tooltip } from '@mui/material';
import { Search } from '@mui/icons-material';
import MenuBook from '@mui/icons-material/MenuBook';
import LinkOff from '@mui/icons-material/LinkOff';
import LocalShipping from '@mui/icons-material/LocalShipping';
import Sync from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { client, MarketCatalogue, MarketSupplier } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableColumn, TableSectionProps } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';
import { isOrderTypeDSD } from '../../../../enums/OrderType';

type Props = {
  onCreateNew: () => void;
  // eslint-disable-next-line no-unused-vars
  onFilterChange?: (_instance: string, _market: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
  /** Splits filters, tiles and table so the caller can slot them into ListPageTemplate. */
  children: (parts: {
    filterBar: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<MarketCatalogue>;
    isInitialLoading: boolean;
  }) => ReactNode;
};

const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const CataloguesListPageContent = ({ onCreateNew, onFilterChange, initialInstance, initialMarket, initialSearchTerm, children }: Props) => {
  const { t } = useTranslation('catalogues');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermissionTo } = useContext(UserContext);
  const canEdit = hasPermissionTo(['ConfigurationAdmin']) || hasPermissionTo(['CatalogueUpdate']);

  const [list, setList] = useState<MarketCatalogue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const { instances, getMarkets, ensureMarketsLoaded, isMarketsLoading, defaultFiltersLoading } = useInstanceMarket();
  const markets = getMarkets(selectedInstance);
  const pageSize = 20;

  // Headline tiles summarise the whole Instance/Market scope (not the current search), fetched once per scope change so they don't slow down search/paging.
  const [statsList, setStatsList] = useState<MarketCatalogue[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);

  const [supplierNames, setSupplierNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedInstance) {
      setStatsList([]);
      setSupplierNames(new Set());
      return;
    }

    setIsStatsLoading(true);
    client.getMarketCatalogues({
      companyId: selectedInstance === 'all' ? undefined : selectedInstance,
      customerTypeId: selectedMarket || undefined,
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          setStatsList(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching catalogue stats:', error))
      .finally(() => setIsStatsLoading(false));

    client.getMarketSuppliers({
      companyId: selectedInstance === 'all' ? undefined : selectedInstance,
      customerTypeId: selectedMarket || undefined,
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          setSupplierNames(new Set(
            (result.data.items ?? [])
              .map((supplier: MarketSupplier) => supplier.supplierName?.toLowerCase())
              .filter(Boolean),
          ));
        }
      })
      .catch((error) => console.error('Error fetching supplier names:', error));
  }, [selectedInstance, selectedMarket]);

  const missingOrderChannelsCount = useMemo(() => (
    statsList.filter((catalogue) => (catalogue.orderTypeIds ?? []).length === 0).length
  ), [statsList]);

  // Suppliers are only meaningful for DSD catalogues, so scope this count to those. A catalogue whose
  // name matches a supplier's name is also considered linked (see isCatalogLinkedToSupplier).
  const missingSuppliersCount = useMemo(() => (
    statsList.filter((catalogue) => (
      (catalogue.orderTypeIds ?? []).some((orderTypeId) => isOrderTypeDSD(orderTypeId)) &&
      (catalogue.supplierIds ?? []).length === 0 &&
      !supplierNames.has(catalogue.name?.toLowerCase())
    )).length
  ), [statsList, supplierNames]);

  const erpMappedCount = useMemo(() => (
    statsList.filter((catalogue) => catalogue.erpInternalId != null).length
  ), [statsList]);

  const fetchCatalogues = async () => {
    setIsLoading(true);
    try {
      const result = await client.getMarketCatalogues({
        companyId: selectedInstance || undefined,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
      });

      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      }
    } catch (error) {
      console.error('Error fetching catalogues:', error);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialMarket, initialSearchTerm]);

  useEffect(() => {
    if (selectedInstance) {
      ensureMarketsLoaded(selectedInstance);
    } else {
      setSelectedMarket('');
    }
  }, [selectedInstance, ensureMarketsLoaded]);

  useEffect(() => {
    if (isMarketsLoading(selectedInstance)) return;

    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
        return;
      }
    }

    if (selectedInstance || selectedInstance === 'all') {
      fetchCatalogues();
    } else {
      setList([]);
      setTotalCount(0);
    }
  }, [markets, currentPage, searchTerm, selectedInstance, selectedMarket]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket(''); // Reset market when instance changes
    setCurrentPage(0);
    onFilterChange?.(event.target.value, '');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, event.target.value);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('all');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('all', '', '');
  };

  const handleRowClick = (catalogue: MarketCatalogue) => {
    if (!canEdit) return;
    const params = new URLSearchParams();
    ['companyId', 'customerTypeId', 'searchTerm'].forEach((key) => {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    });
    navigate(`/procurement/catalogues/edit/${catalogue.customerGroupId}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const columns: TableColumn<MarketCatalogue>[] = [
    {
      id: 'catalogueName',
      label: t('management.catalogueName'),
      render: (row) => (
        <Tooltip title={row.name}
          placement="top">
          <span>{truncateText(row.name)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'instanceName',
      label: t('management.instanceName'),
      render: (row) => (
        <Tooltip title={row.instanceName}
          placement="top">
          <span>{truncateText(row.instanceName)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'marketName',
      label: t('management.marketName'),
      render: (row) => (
        <Tooltip title={row.marketName}
          placement="top">
          <span>{truncateText(row.marketName)}</span>
        </Tooltip>
      ),
    },
  ];

  const table: TableSectionProps<MarketCatalogue> = {
    title: t('management.catalogues'),
    columns,
    rows: list,
    getRowId: (row) => row.customerGroupId,
    onRowClick: canEdit ? handleRowClick : undefined,
    recordCount: totalCount,
    recordCountLabel: t('management.catalogues'),
    loading: isLoading,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('management.catalogues')}
        searchTerm={searchInput}
        onClear={handleClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('management.createNew')}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    onPageChange: handlePageChange,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('management.search')}
          placeholder={t('management.searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('management.clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('management.search')}
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('management.filterByInstance')}
          </InputLabel>
          <Select
            value={instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
            onChange={handleInstanceChange}
            label={t('management.filterByInstance')}
            displayEmpty
            notched
            renderValue={(value) => {
              if (value === '') {
                return <span>{t('management.allInstances')}</span>;
              }

              return instances?.find(i => i.id.toString() === value)?.name || '';
            }}
          >
            <MenuItem value="all">
              {t('management.allInstances')}
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem
                key={instance.id}
                value={instance.id.toString()}
              >
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('management.filterByMarket')}
          </InputLabel>
          <Select
            value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
            onChange={handleMarketChange}
            label={t('management.filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value="">
              {t('management.allMarkets')}
            </MenuItem>
            {markets.map((market) => (
              <MenuItem
                key={market.customerTypeId}
                value={market.customerTypeId.toString()}
              >
                {market.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile
        key="totalCatalogues"
        label={t('management.tileTotalCatalogues', 'Total Catalogues')}
        value={statsList.length}
        description={t('management.tileTotalCataloguesDescription', 'All catalogues in the selected Instance/Market')}
        icon={<MenuBook />}
        variant="primary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingOrderChannels"
        label={t('management.tileMissingOrderChannels', 'Missing Order Channels')}
        value={missingOrderChannelsCount}
        description={t('management.tileMissingOrderChannelsDescription', 'Catalogues with no order channel assigned - can\'t be ordered')}
        icon={<LinkOff />}
        variant="warning"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingSuppliers"
        label={t('management.tileMissingSuppliers', 'DSD Missing Suppliers')}
        value={missingSuppliersCount}
        description={t('management.tileMissingSuppliersDescription', 'DSD catalogues with no supplier assigned')}
        icon={<LocalShipping />}
        variant="error"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="erpMapped"
        label={t('management.tileErpMappedCountLabel', 'ERP Mapped')}
        value={`${erpMappedCount}/${statsList.length}`}
        description={t('management.tileErpMappedCountDescription', 'Catalogues with an ERP mapping configured')}
        icon={<Sync />}
        variant="secondary"
        loading={isStatsLoading}
      />,
    ],
  };

  // Still resolving the default instance/market before the first fetch even starts - keep showing the skeleton, not a zero-data flash.
  return <>{children({ filterBar, headlineContent, table, isInitialLoading: (defaultFiltersLoading || isLoading) && !hasLoadedOnce })}</>;
};

export default CataloguesListPageContent;

